import apiClient from "./axiosConfig";

// Get products list with search and pagination
export const getProductsList = async (params = {}) => {
  try {
    const { search = '', page = 1, page_size = 10 } = params;
    const queryParams = new URLSearchParams();
    
    if (search) queryParams.append('search', search);
    if (page) queryParams.append('page', page);
    if (page_size) queryParams.append('page_size', page_size);
    
    const response = await apiClient.get(`/products/list/?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

// Get product detail
export const getProductDetail = async (productId) => {
  try {
    const response = await apiClient.get(`/products/detail/${productId}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product detail:', error);
    throw error;
  }
};

// Create new product
export const createProduct = async (productData) => {
  try {
    const formData = new FormData();
    
    // Add basic product fields
    formData.append('category', productData.category || '');
    formData.append('brand', productData.brandName); // Changed from 'brand_name' to 'brand' (foreign key)
    formData.append('name', productData.modelName); // Changed from 'model_name' to 'name'
    
    if (productData.ram) formData.append('ram', productData.ram);
    if (productData.suffix) formData.append('suffix', productData.suffix);
    if (productData.storage) formData.append('storage', productData.storage);
    if (productData.tag) formData.append('unique_identifier', productData.tag);
    if (productData.location) formData.append('location', productData.location);
    
    // Add device condition flag: used_mobile (false for new, true for used)
    const usedMobile = productData.isUsedDevice === true;
    formData.append('used_mobile', usedMobile);
    
    // Add additional_info if provided
    if (productData.additional_info) {
      formData.append('additional_info', JSON.stringify(productData.additional_info));
    }
    
    // Add image if provided
    if (productData.thumbnailImage && typeof productData.thumbnailImage === 'object') {
      formData.append('image', productData.thumbnailImage);
    }
    
    // Add variant features as variants array
    const variants = [];
    if (productData.variantFeatures && productData.variantFeatures.length > 0) {
      const variantObject = {};
      productData.variantFeatures.forEach(feature => {
        if (feature.name && feature.value) {
          variantObject[feature.name] = feature.value;
        }
      });
      if (Object.keys(variantObject).length > 0) {
        variants.push(variantObject);
      }
    }
    // Always append variants (empty array if no variants)
    formData.append('variants', JSON.stringify(variants));
    
    const response = await apiClient.post('/products/create/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error creating product:', error);
    
    // Handle validation errors
    if (error.response && error.response.data) {
      const errorData = error.response.data;
      if (errorData.status === 'error' && errorData.errors) {
        // Create a more detailed error object
        const validationError = new Error(errorData.message || 'Validation failed');
        validationError.validationErrors = errorData.errors;
        validationError.isValidationError = true;
        throw validationError;
      }
    }
    
    throw error;
  }
};

// Update product
export const updateProduct = async (productId, productData) => {
  try {
    const formData = new FormData();
    
    // Add basic product fields
    formData.append('category', productData.category || '');
    formData.append('brand', productData.brandName); // Changed from 'brand_name' to 'brand' (foreign key)
    formData.append('name', productData.modelName); // Changed from 'model_name' to 'name'
    
    // Always append these fields, even if empty (for PATCH API to clear existing values)
    formData.append('ram', productData.ram || '');
    formData.append('suffix', productData.suffix || '');
    formData.append('storage', productData.storage || '');
    if (productData.tag) formData.append('unique_identifier', productData.tag);
    if (productData.location) formData.append('location', productData.location);
    
    // Add device condition flag: used_mobile
    // - If "New Device" is checked: send false
    // - If "Used Device" is checked: send true
    // - If both are unchecked: send empty string (to clear the value)
    if (productData.isNewDevice === true) {
      formData.append('used_mobile', false);
    } else if (productData.isUsedDevice === true) {
      formData.append('used_mobile', true);
    } else {
      formData.append('used_mobile', ''); // Send empty string when both unchecked
    }
    
    // Add additional_info if provided
    if (productData.additional_info) {
      formData.append('additional_info', JSON.stringify(productData.additional_info));
    }
    
    // Add image if provided (new file)
    if (productData.thumbnailImage && typeof productData.thumbnailImage === 'object') {
      formData.append('image', productData.thumbnailImage);
    }
    
    // Add variant features as variants array
    // Always send variants array, even if empty (for PATCH API to clear existing variants)
    const variants = [];
    if (productData.variantFeatures && productData.variantFeatures.length > 0) {
      const variantObject = {};
      productData.variantFeatures.forEach(feature => {
        if (feature.name && feature.value) {
          variantObject[feature.name] = feature.value;
        }
      });
      if (Object.keys(variantObject).length > 0) {
        variants.push(variantObject);
      }
    }
    // Always append variants (empty array if no variants)
    formData.append('variants', JSON.stringify(variants));
    
    const response = await apiClient.patch(`/products/update/${productId}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error updating product:', error);
    
    // Handle validation errors
    if (error.response && error.response.data) {
      const errorData = error.response.data;
      if (errorData.status === 'error' && errorData.errors) {
        // Create a more detailed error object
        const validationError = new Error(errorData.message || 'Validation failed');
        validationError.validationErrors = errorData.errors;
        validationError.isValidationError = true;
        throw validationError;
      }
    }
    
    throw error;
  }
};

// Delete product
export const deleteProduct = async (productId) => {
  try {
    const response = await apiClient.delete(`/products/delete/${productId}/`);
    return response.data;
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};

// Get product statistics
export const getProductStats = async () => {
  try {
    const response = await apiClient.get('/products/count/');
    
    // Handle different response structures
    const data = response.data?.data || response.data || {};
    
    return data;
  } catch (error) {
    console.error('Error fetching product stats:', error);
    throw error;
  }
};

// Add low stock threshold
export const addLowStockThreshold = async (productId, minimumRequiredStock) => {
  try {
    const response = await apiClient.post('/products/low-stocks/add/', {
      product_id: productId,
      minimum_required_stock: minimumRequiredStock
    });
    return response.data;
  } catch (error) {
    console.error('Error adding low stock threshold:', error);
    throw error;
  }
};

// Update low stock threshold
export const updateLowStockThreshold = async (lowStockId, minimumRequiredStock) => {
  try {
    const response = await apiClient.patch(`/products/low-stocks/${lowStockId}/`, {
      minimum_required_stock: minimumRequiredStock
    });
    return response.data;
  } catch (error) {
    console.error('Error updating low stock threshold:', error);
    throw error;
  }
};